/** Appearance tweaks from the platform redesign handoff (surface / backdrop / agency accent). */

export type SurfaceStyle = 'frosted' | 'crystal' | 'solid';
export type BackdropMood = 'aurora' | 'candy' | 'mono';
export type AgencyAccent = 'violet' | 'ocean' | 'sunset' | 'jade';
export type PortalId = 'agency' | 'brand' | 'kol' | 'admin';

export const SURFACE_KEY = 'mykol_surface';
export const BACKDROP_KEY = 'mykol_backdrop';
export const AGENCY_ACCENT_KEY = 'mykol_agency_accent';

export const DEFAULT_SURFACE: SurfaceStyle = 'frosted';
export const DEFAULT_BACKDROP: BackdropMood = 'aurora';
export const DEFAULT_AGENCY_ACCENT: AgencyAccent = 'violet';

function isSurface(v: unknown): v is SurfaceStyle {
  return v === 'frosted' || v === 'crystal' || v === 'solid';
}
function isBackdrop(v: unknown): v is BackdropMood {
  return v === 'aurora' || v === 'candy' || v === 'mono';
}
function isAgencyAccent(v: unknown): v is AgencyAccent {
  return v === 'violet' || v === 'ocean' || v === 'sunset' || v === 'jade';
}

export function readStoredSurface(): SurfaceStyle {
  const v = localStorage.getItem(SURFACE_KEY);
  return isSurface(v) ? v : DEFAULT_SURFACE;
}

export function readStoredBackdrop(): BackdropMood {
  const v = localStorage.getItem(BACKDROP_KEY);
  return isBackdrop(v) ? v : DEFAULT_BACKDROP;
}

export function readStoredAgencyAccent(): AgencyAccent {
  const v = localStorage.getItem(AGENCY_ACCENT_KEY);
  return isAgencyAccent(v) ? v : DEFAULT_AGENCY_ACCENT;
}

export function applySurface(surface: SurfaceStyle) {
  localStorage.setItem(SURFACE_KEY, surface);
  document.documentElement.dataset.surface = surface;
  document.querySelectorAll('[data-portal-shell]').forEach((el) => {
    (el as HTMLElement).dataset.surface = surface;
  });
}

export function applyBackdrop(backdrop: BackdropMood) {
  localStorage.setItem(BACKDROP_KEY, backdrop);
  document.documentElement.dataset.backdrop = backdrop;
  document.querySelectorAll('[data-portal-shell]').forEach((el) => {
    (el as HTMLElement).dataset.backdrop = backdrop;
  });
}

export function applyAgencyAccent(accent: AgencyAccent) {
  localStorage.setItem(AGENCY_ACCENT_KEY, accent);
  document.documentElement.dataset.accent = accent;
  document.querySelectorAll('[data-portal-shell="agency"]').forEach((el) => {
    (el as HTMLElement).dataset.accent = accent;
  });
}

export function bootstrapAppearance() {
  applySurface(readStoredSurface());
  applyBackdrop(readStoredBackdrop());
  applyAgencyAccent(readStoredAgencyAccent());
}

export function applyAppearanceFromSettings(settings: Record<string, unknown> | null | undefined) {
  if (!settings || typeof settings !== 'object') return;
  if (isSurface(settings.surfaceStyle)) applySurface(settings.surfaceStyle);
  if (isBackdrop(settings.backdrop)) applyBackdrop(settings.backdrop);
  if (isAgencyAccent(settings.agencyAccent)) applyAgencyAccent(settings.agencyAccent);
}

/** Props to spread onto each portal layout root. */
export function portalShellProps(portal: PortalId) {
  return {
    'data-portal-shell': portal,
    'data-portal': portal,
    'data-theme':
      typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
        ? 'dark'
        : 'light',
    'data-surface': readStoredSurface(),
    'data-backdrop': readStoredBackdrop(),
    ...(portal === 'agency' ? { 'data-accent': readStoredAgencyAccent() } : {}),
  } as const;
}
